import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import {
  Check,
  Edit,
  Mail,
  Plus,
  RefreshCw,
  Trash2,
  UserPlus,
  Users,
  X
} from 'lucide-react'
import { AppLayout } from '../../components/AppLayout'
import { Card } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import { Badge } from '../../components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '../../components/ui/dialog'
import { useTranslation } from 'react-i18next'
import {
  acceptInvitation,
  addGroupMembers,
  createGroup,
  deleteGroup,
  getGroup,
  inviteGroupMember,
  leaveGroup,
  listGroups,
  listSentPendingInvitations,
  listMyInvitations,
  rejectInvitation,
  removeGroupMember,
  updateGroupName,
  updateGroupMemberEmail
} from '../../api/groups'

function parseEmailList(raw) {
  return Array.from(
    new Set(
      String(raw || '')
        .split(/[\n,; ]+/)
        .map(item => item.trim().toLowerCase())
        .filter(Boolean)
    )
  )
}

function memberStatusLabel(status, t) {
  if (status === 'ATIVO') return t('active')
  if (status === 'NAO_CADASTRADO') return t('notRegistered')
  if (status === 'CONVIDADO_PENDENTE') return t('invitedPending')
  if (status === 'RECUSADO') return t('rejected')
  return status
}

function memberStatusBadgeClass(status) {
  if (status === 'ATIVO') return 'bg-green-100 text-green-700'
  if (status === 'NAO_CADASTRADO') return 'bg-amber-100 text-amber-700'
  if (status === 'CONVIDADO_PENDENTE') return 'bg-blue-100 text-blue-700'
  if (status === 'RECUSADO') return 'bg-red-100 text-red-700'
  return 'bg-slate-100 text-slate-700'
}

export default function Groups() {
  const { t, i18n } = useTranslation()
  const language = i18n.language
  const [groups, setGroups] = useState([])
  const [isLoadingGroups, setIsLoadingGroups] = useState(false)
  const [selectedGroupId, setSelectedGroupId] = useState('')
  const [groupDetail, setGroupDetail] = useState(null)
  const [invitations, setInvitations] = useState([])
  const [sentPendingInvitations, setSentPendingInvitations] = useState([])
  const [isPendingInvitationsDialogOpen, setIsPendingInvitationsDialogOpen] =
    useState(false)
  const [isGroupsDialogOpen, setIsGroupsDialogOpen] = useState(false)
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isManageDialogOpen, setIsManageDialogOpen] = useState(false)

  const [newGroupName, setNewGroupName] = useState('')
  const [newGroupEmails, setNewGroupEmails] = useState('')
  const [renameInput, setRenameInput] = useState('')
  const [newMembersInput, setNewMembersInput] = useState('')
  const [editingMemberId, setEditingMemberId] = useState(null)

  const [editedEmail, setEditedEmail] = useState('')

  const selectedGroup = useMemo(
    () =>
      groups.find(group => String(group._id) === String(selectedGroupId)) ||
      null,
    [groups, selectedGroupId]
  )

  const totalPendingInvitations =
    invitations.length + sentPendingInvitations.length
  const groupsSummary = useMemo(() => {
    return groups.reduce(
      (acc, group) => {
        acc.total += 1
        acc.active += Number(group.participantsActive || 0)
        acc.notRegistered += Number(group.participantsNotRegistered || 0)
        return acc
      },
      { total: 0, active: 0, notRegistered: 0 }
    )
  }, [groups])

  const refreshSentPendingInvitations = useCallback(async (groupsList = []) => {
    if (!Array.isArray(groupsList) || groupsList.length === 0) {
      setSentPendingInvitations([])
      return
    }

    try {
      const groupIds = groupsList
        .map(group => String(group?._id || '').trim())
        .filter(Boolean)

      if (groupIds.length === 0) {
        setSentPendingInvitations([])
        return
      }

      const response = await listSentPendingInvitations(groupIds)
      const pending = Array.isArray(response)
        ? response
        : Array.isArray(response?.invitations)
          ? response.invitations
          : []

      setSentPendingInvitations(pending)
    } catch (error) {
      console.error('Erro ao buscar convites pendentes enviados:', error)
      setSentPendingInvitations([])
    }
  }, [])

  const refreshGroups = useCallback(
    async (selectGroupId = null) => {
      setIsLoadingGroups(true)
      try {
        const list = await listGroups()
        setGroups(list)
        await refreshSentPendingInvitations(list)

        const targetId = selectGroupId || selectedGroupId || list[0]?._id || ''
        setSelectedGroupId(targetId ? String(targetId) : '')
      } catch (error) {
        toast.error(error.response?.data?.error || t('failedToLoadGroups'))
      } finally {
        setIsLoadingGroups(false)
      }
    },
    [refreshSentPendingInvitations, selectedGroupId]
  )

  const refreshInvitations = useCallback(async () => {
    try {
      const list = await listMyInvitations()
      setInvitations(list)
    } catch (error) {
      toast.error(error.response?.data?.error || t('failedToLoadInvitations'))
    }
  }, [])

  const refreshGroupDetail = useCallback(async id => {
    if (!id) {
      setGroupDetail(null)
      return
    }

    try {
      const detail = await getGroup(id)
      setGroupDetail(detail)
      setRenameInput(detail?.group?.nome || '')
    } catch (error) {
      setGroupDetail(null)
      toast.error(error.response?.data?.error || t('failedToLoadGroup'))
    }
  }, [])

  const refreshGroupsAndInvitations = useCallback(
    async (selectGroupId = null) => {
      await Promise.allSettled([
        refreshGroups(selectGroupId),
        refreshInvitations()
      ])
    },
    [refreshGroups, refreshInvitations]
  )

  const refreshGroupSnapshot = useCallback(
    async groupId => {
      await Promise.allSettled([
        refreshGroupDetail(groupId),
        refreshGroups(groupId)
      ])
    },
    [refreshGroupDetail, refreshGroups]
  )

  useEffect(() => {
    refreshGroupsAndInvitations()
  }, [refreshGroupsAndInvitations])

  useEffect(() => {
    refreshGroupDetail(selectedGroupId)
  }, [selectedGroupId, refreshGroupDetail])

  const openManageGroupDialog = useCallback(
    async groupId => {
      const normalizedGroupId = String(groupId)

      if (normalizedGroupId === String(selectedGroupId)) {
        await refreshGroupDetail(normalizedGroupId)
      } else {
        setSelectedGroupId(normalizedGroupId)
      }

      setIsManageDialogOpen(true)
    },
    [refreshGroupDetail, selectedGroupId]
  )

  const handleCreateGroup = async () => {
    const nome = newGroupName.trim()
    const emails = parseEmailList(newGroupEmails)

    if (!nome) {
      toast.error(t('enterGroupName'))
      return
    }

    if (emails.length < 1) {
      toast.error(t('enterParticipantEmail'))
      return
    }

    try {
      const response = await createGroup({ nome, emails })
      const createdGroupId = response?.group?._id

      // Dispara convites para os membros recém-adicionados (mesmo loop que `handleAddMembers`).
      const newlyAddedMembers = Array.isArray(response?.members)
        ? response.members
        : []
      let invitesSent = 0
      for (const member of newlyAddedMembers) {
        if (!member?._id) continue
        if (member.papel === 'OWNER') continue
        if (member.status === 'ATIVO') continue

        try {
          const inviteResponse = await inviteGroupMember(
            createdGroupId,
            member._id
          )
          if (inviteResponse?.member?.status === 'CONVIDADO_PENDENTE') {
            invitesSent += 1
          }
        } catch {
          // mantém fluxo; UI exibirá status real ao recarregar
        }
      }

      if (invitesSent > 0) {
        toast.success(t('groupCreatedWithInvites', { count: invitesSent }))
      } else {
        toast.success(t('groupCreatedSuccessfully'))
      }

      setIsCreateDialogOpen(false)
      setNewGroupName('')
      setNewGroupEmails('')
      await refreshGroupsAndInvitations(createdGroupId)
    } catch (error) {
      toast.error(error.response?.data?.error || t('failedToCreateGroup'))
    }
  }

  const handleRenameGroup = async () => {
    const nome = renameInput.trim()
    if (!groupDetail?.group?._id || !nome) return

    try {
      await updateGroupName(groupDetail.group._id, { nome })
      toast.success(t('groupUpdatedSuccessfully'))
      await refreshGroupSnapshot(groupDetail.group._id)
    } catch (error) {
      toast.error(error.response?.data?.error || t('failedToRenameGroup'))
    }
  }

  const handleAddMembers = async () => {
    if (!groupDetail?.group?._id) return
    const emails = parseEmailList(newMembersInput)

    if (emails.length === 0) {
      toast.error(t('enterValidEmails'))
      return
    }

    try {
      const previousMemberEmails = new Set(
        (groupDetail.members || []).map(member =>
          String(member.email || '').toLowerCase()
        )
      )

      const response = await addGroupMembers(groupDetail.group._id, { emails })
      const members = Array.isArray(response?.members) ? response.members : []

      const normalizedInputEmails = new Set(
        emails.map(email => String(email || '').toLowerCase())
      )
      const newlyAddedMembers = members.filter(member => {
        const memberEmail = String(member.email || '').toLowerCase()
        return (
          normalizedInputEmails.has(memberEmail) &&
          !previousMemberEmails.has(memberEmail)
        )
      })

      let invitesSent = 0
      let inviteSkippedAsActive = 0

      for (const member of newlyAddedMembers) {
        if (member.papel === 'OWNER') continue
        if (member.status === 'ATIVO') {
          inviteSkippedAsActive += 1
          continue
        }

        try {
          const inviteResponse = await inviteGroupMember(
            groupDetail.group._id,
            member._id
          )
          const updatedMemberStatus = inviteResponse?.member?.status

          if (updatedMemberStatus === 'CONVIDADO_PENDENTE') {
            invitesSent += 1
          } else {
            inviteSkippedAsActive += 1
          }
        } catch {
          // mantém fluxo; erro específico aparecerá na atualização de detalhes
        }
      }

      setNewMembersInput('')
      await refreshGroupSnapshot(groupDetail.group._id)

      if (invitesSent > 0) {
        toast.success(t('participantsAddedWithInvites', { count: invitesSent }))
      } else if (inviteSkippedAsActive > 0) {
        toast.success(t('participantsAddedAndActivated'))
      } else {
        toast.success(t('participantsAdded'))
      }
    } catch (error) {
      toast.error(error.response?.data?.error || t('failedToAddParticipants'))
    }
  }

  const handleLeaveGroup = async () => {
    if (!groupDetail?.group?._id) return

    const confirmed = window.confirm(t('confirmLeaveGroup'))

    if (!confirmed) return

    try {
      await leaveGroup(groupDetail.group._id)
      toast.success(t('leftGroupSuccessfully'))
      setIsManageDialogOpen(false)
      await refreshGroupsAndInvitations()
    } catch (error) {
      toast.error(error.response?.data?.error || t('failedToLeaveGroup'))
    }
  }

  const handleDeleteGroup = async () => {
    if (!groupDetail?.group?._id) return

    const confirmed = window.confirm(t('confirmDeleteGroup'))

    if (!confirmed) return

    try {
      await deleteGroup(groupDetail.group._id)
      toast.success(t('groupDeletedSuccessfully'))
      setIsManageDialogOpen(false)
      await refreshGroupsAndInvitations()
    } catch (error) {
      toast.error(error.response?.data?.error || t('failedToDeleteGroup'))
    }
  }

  const handleRemoveMember = async (memberId, isOwner) => {
    if (!groupDetail?.group?._id || !memberId || isOwner) return

    const confirmed = window.confirm(t('confirmRemoveParticipant'))

    if (!confirmed) return

    try {
      await removeGroupMember(groupDetail.group._id, memberId)
      toast.success(t('participantRemoved'))
      await refreshGroupSnapshot(groupDetail.group._id)
    } catch (error) {
      toast.error(error.response?.data?.error || t('failedToRemoveParticipant'))
    }
  }

  const handleInviteMember = async memberId => {
    if (!groupDetail?.group?._id || !memberId) return

    const targetMember = (groupDetail?.members || []).find(
      member => String(member._id) === String(memberId)
    )
    if (targetMember?.status === 'ATIVO') {
      toast.error(t('activeParticipantCannotBeInvitedAgain'))
      return
    }

    try {
      setGroupDetail(prev => {
        if (!prev?.members) return prev
        return {
          ...prev,
          members: prev.members.map(member =>
            String(member._id) === String(memberId)
              ? { ...member, status: 'CONVIDADO_PENDENTE' }
              : member
          )
        }
      })

      const response = await inviteGroupMember(groupDetail.group._id, memberId)
      const message = response?.message
      toast.success(message || t('invitationSent'))
      await refreshGroupSnapshot(groupDetail.group._id)
    } catch (error) {
      toast.error(error.response?.data?.error || t('failedToInviteParticipant'))
    }
  }

  const handleUpdateMemberEmail = async memberId => {
    if (!groupDetail?.group?._id || !memberId) return

    try {
      await updateGroupMemberEmail(groupDetail.group._id, memberId, {
        email: editedEmail
      })

      toast.success(t('participantEmailUpdated'))

      setEditingMemberId(null)
      setEditedEmail('')

      await refreshGroupSnapshot(groupDetail.group._id)
    } catch (error) {
      toast.error(
        error.response?.data?.error || t('failedToUpdateParticipantEmail')
      )
    }
  }

  const handleAcceptInvitation = async invitationId => {
    try {
      await acceptInvitation(invitationId)
      toast.success(t('invitationAccepted'))
      await refreshGroupsAndInvitations()
    } catch (error) {
      toast.error(error.response?.data?.error || t('failedToAcceptInvitation'))
    }
  }

  const handleRejectInvitation = async invitationId => {
    try {
      await rejectInvitation(invitationId)
      toast.success(t('invitationRejected'))
      await refreshGroupsAndInvitations()
    } catch (error) {
      toast.error(error.response?.data?.error || t('failedToRejectInvitation'))
    }
  }

  return (
    <AppLayout
      title={t('groups')}
      subtitle={t('manageParticipantsAndInvitations')}
      icon={Users}
      headerActions={
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => {
              refreshGroupsAndInvitations()
            }}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            {t('refresh')}
          </Button>
          <Dialog
            open={isCreateDialogOpen}
            onOpenChange={setIsCreateDialogOpen}
          >
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700">
                <Plus className="w-4 h-4 mr-2" />
                {t('newGroup')}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t('createGroup')}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label>{t('groupName')}</Label>
                  <Input
                    value={newGroupName}
                    onChange={event => setNewGroupName(event.target.value)}
                    placeholder={t('groupNameExample')}
                  />
                </div>
                <div className="space-y-1">
                  <Label>{t('participantEmails')}</Label>
                  <Input
                    value={newGroupEmails}
                    onChange={event => setNewGroupEmails(event.target.value)}
                    placeholder={t('participantEmailsExample')}
                  />
                  <p className="text-xs text-slate-500">
                    {t('emailSeparatorHint')}
                  </p>
                </div>
                <Button className="w-full" onClick={handleCreateGroup}>
                  {t('createGroup')}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      }
      mainClassName="space-y-6"
    >
      <Card className="p-5">
        <h3 className="text-lg font-semibold mb-4">
          {t('pendingInvitations')}
        </h3>
        <div className="flex flex-col gap-4 rounded-md border border-slate-200 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-2">
            <p className="text-sm text-slate-700">
              {t('pendingInvitationsCount', { count: totalPendingInvitations })}
            </p>
            <div className="flex flex-wrap gap-2">
              <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
                {t('received')}
              </Badge>
              <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100">
                {t('sent')}
              </Badge>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() => setIsPendingInvitationsDialogOpen(true)}
            disabled={totalPendingInvitations === 0}
          >
            {t('viewInvitations')}
          </Button>
        </div>
      </Card>

      <Card className="p-4">
        <h3 className="text-lg font-semibold mb-3">{t('yourGroups')}</h3>
        <div className="flex flex-col gap-4 rounded-md border border-slate-200 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-2">
            {isLoadingGroups ? (
              <p className="text-sm text-slate-500">{t('loadingGroups')}</p>
            ) : (
              <p className="text-sm text-slate-700">
                {t('registeredGroupsCount', { count: groupsSummary.total })}
              </p>
            )}
            {!isLoadingGroups && (
              <div className="flex flex-wrap gap-2">
                <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                  {t('active')}
                </Badge>
                <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
                  {t('notRegistered')} : {groupsSummary.notRegistered}
                </Badge>
              </div>
            )}
          </div>
          <Button
            variant="outline"
            onClick={() => setIsGroupsDialogOpen(true)}
            disabled={isLoadingGroups || groupsSummary.total === 0}
          >
            {t('viewGroups')}
          </Button>
        </div>
      </Card>

      <Dialog open={isManageDialogOpen} onOpenChange={setIsManageDialogOpen}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>
              {t('manageGroup')}
              {selectedGroup ? `: ${selectedGroup.nome}` : ''}
            </DialogTitle>
          </DialogHeader>
          {!selectedGroup || !groupDetail ? (
            <p className="text-sm text-slate-500">{t('selectGroupToManage')}</p>
          ) : (
            <div className="space-y-5 max-h-[70vh] overflow-y-auto pr-1">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto] md:items-end">
                <div className="space-y-1">
                  <Label>{t('groupName')}</Label>
                  <Input
                    value={renameInput}
                    onChange={event => setRenameInput(event.target.value)}
                  />
                </div>
                <Button onClick={handleRenameGroup}>{t('saveName')}</Button>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto] md:items-end">
                <div className="space-y-1">
                  <Label>{t('addParticipantsEmails')}</Label>
                  <Input
                    value={newMembersInput}
                    onChange={event => setNewMembersInput(event.target.value)}
                    placeholder={t('participantEmailsExample')}
                  />
                </div>
                <Button variant="outline" onClick={handleAddMembers}>
                  <UserPlus className="w-4 h-4 mr-2" />
                  {t('add')}
                </Button>
              </div>

              <div className="space-y-2">
                <h4 className="font-semibold text-slate-900">
                  {t('groupParticipants')}
                </h4>
                {groupDetail.members?.length ? (
                  <div className="space-y-2">
                    {groupDetail.members.map(member => {
                      const isOwner = member.papel === 'OWNER'
                      return (
                        <div
                          key={member._id}
                          className="flex flex-col gap-2 rounded-md border border-slate-200 p-3 sm:flex-row sm:items-center sm:justify-between"
                        >
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-slate-800">
                                {member.nome || member.email}
                              </p>
                              {isOwner && (
                                <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100">
                                  {t('owner')}
                                </Badge>
                              )}
                            </div>
                            {editingMemberId === member._id ? (
                              <div className="flex items-center gap-2">
                                <Input
                                  value={editedEmail}
                                  onChange={event =>
                                    setEditedEmail(event.target.value)
                                  }
                                />

                                <Button
                                  size="sm"
                                  onClick={() =>
                                    handleUpdateMemberEmail(member._id)
                                  }
                                >
                                  <Check className="w-4 h-4" />
                                </Button>

                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setEditingMemberId(null)
                                    setEditedEmail('')
                                  }}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            ) : (
                              <p className="text-xs text-slate-600">
                                {member.email}
                              </p>
                            )}
                            <Badge
                              className={`${memberStatusBadgeClass(member.status)} hover:opacity-95`}
                            >
                              {memberStatusLabel(member.status, t)}
                            </Badge>
                          </div>

                          <div className="flex items-center gap-2">
                            {!isOwner && member.status !== 'ATIVO' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setEditingMemberId(member._id)
                                  setEditedEmail(member.email)
                                }}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                            )}
                            {(member.status === 'NAO_CADASTRADO' ||
                              member.status === 'RECUSADO') &&
                              !isOwner && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleInviteMember(member._id)}
                                >
                                  <Mail className="w-4 h-4 mr-1" />
                                  {t('invite')}
                                </Button>
                              )}
                            {!isOwner && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                  handleRemoveMember(member._id, isOwner)
                                }
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">
                    {t('noParticipantsFound')}
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="border-t border-slate-200 pt-4 mt-4 flex flex-wrap gap-2">
            {(() => {
              const stored =
                localStorage.getItem('SistemaUser') ||
                sessionStorage.getItem('SistemaUser')
              const currentUserId = stored
                ? JSON.parse(stored).id || JSON.parse(stored)._id
                : null
              const isOwner =
                String(groupDetail?.group?.ownerId || '') ===
                String(currentUserId || '')
              return isOwner ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                  onClick={handleDeleteGroup}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  {t('deleteGroup')}
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 border-amber-200"
                  onClick={handleLeaveGroup}
                >
                  <X className="w-4 h-4 mr-1" />
                  {t('leaveGroup')}
                </Button>
              )
            })()}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isPendingInvitationsDialogOpen}
        onOpenChange={setIsPendingInvitationsDialogOpen}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t('pendingInvitations')}</DialogTitle>
          </DialogHeader>
          {totalPendingInvitations === 0 ? (
            <p className="text-sm text-slate-500">
              {t('noPendingInvitations')}
            </p>
          ) : (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
              {invitations.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-wide text-slate-500">
                    {t('receivedByYou')}
                  </p>
                  {invitations.map(invitation => (
                    <div
                      key={invitation._id}
                      className="flex flex-col gap-2 rounded-md border border-slate-200 p-3 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="text-sm">
                        <p className="font-medium text-slate-800">
                          {invitation.groupName || '-'}
                        </p>
                        <p className="text-slate-600">
                          {invitation.nome ||
                            invitation.memberName ||
                            invitation.email}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRejectInvitation(invitation._id)}
                        >
                          <X className="w-4 h-4 mr-1" />
                          {t('reject')}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleAcceptInvitation(invitation._id)}
                        >
                          <Check className="w-4 h-4 mr-1" />
                          {t('accept')}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {sentPendingInvitations.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-wide text-slate-500">
                    {t('sentByYou')}
                  </p>
                  {sentPendingInvitations.map(invitation => (
                    <div
                      key={`${invitation.groupId}-${invitation.memberId}`}
                      className="flex flex-col gap-2 rounded-md border border-slate-200 p-3 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="text-sm">
                        <p className="font-medium text-slate-800">
                          {invitation.groupName || '-'}
                        </p>
                        <p className="text-slate-600">
                          {invitation.nome || invitation.email}
                        </p>
                      </div>
                      <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
                        {t('invitedPending')}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={isGroupsDialogOpen} onOpenChange={setIsGroupsDialogOpen}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>{t('yourGroups')}</DialogTitle>
          </DialogHeader>
          {isLoadingGroups ? (
            <p className="text-sm text-slate-500">{t('loadingGroups')}</p>
          ) : groups.length === 0 ? (
            <p className="text-sm text-slate-500">{t('noGroupsYet')}</p>
          ) : (
            <div className="grid max-h-[70vh] grid-cols-1 gap-2 overflow-y-auto pr-1 md:grid-cols-2 xl:grid-cols-3">
              {groups.map(group => (
                <div
                  key={group._id}
                  className="rounded-md border border-slate-200 p-3 text-left transition hover:border-slate-300 hover:bg-slate-50"
                >
                  <p className="font-medium text-slate-800">{group.nome}</p>
                  <p className="text-xs text-slate-600">
                    {t('participants')}: {group.participantsCount || 0}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1">
                    <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                      {t('active')}: {group.participantsActive || 0}
                    </Badge>
                    <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
                      {t('notRegistered')}:{' '}
                      {group.participantsNotRegistered || 0}
                    </Badge>
                  </div>
                  <div className="mt-3">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setIsGroupsDialogOpen(false)
                        openManageGroupDialog(group._id)
                      }}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      {t('edit')}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  )
}
